<template>
  <div class="painel-conexao-caixa">
    <div class="text-caption text-grey q-mb-sm">
      Importar do Conexão Parceiros Caixa
    </div>

    <!-- Consultando: só o progresso; a consulta acorda o PC da lotérica e demora. -->
    <div v-if="consultando">
      <q-linear-progress indeterminate color="primary" rounded class="q-mb-sm" />
      <div class="text-primary text-caption row items-center no-wrap">
        <q-spinner size="1em" class="q-mr-xs" />
        {{ mensagemProgresso || 'Consultando relatório no portal Parceiros Caixa. Isso pode levar alguns minutos…' }}
      </div>
    </div>

    <!-- Erro: mensagem fixa em vermelho; demais componentes voltam liberados. -->
    <q-banner
      v-else-if="temErro && erro"
      class="bg-negative text-white q-mb-sm"
      rounded
      dense
    >
      <template #avatar><q-icon name="error" /></template>
      {{ erro }}
    </q-banner>

    <div v-if="relatorioCarregado && !consultando && textoBase" class="text-caption text-primary q-mb-sm faixa-base-sessao">
      <q-icon name="history" class="q-mr-xs" />{{ textoBase }}
    </div>

    <div v-if="relatorioCarregado && !consultando && precisaLiberacao && !podeLiberar" class="text-caption text-negative q-mb-sm aviso-liberacao">
      <q-icon name="lock" class="q-mr-xs" />{{ textoLiberacao }}
    </div>

    <div v-if="relatorioCarregado && !consultando && !comPendencias" class="text-positive q-mb-sm">
      <q-icon name="check_circle" /> Relatório importado — use "Enviar para o caixa" abaixo
    </div>

    <q-banner
      v-if="comPendencias"
      class="bg-orange-2 text-brown-10 q-mt-md q-mb-sm pendencias-importacao-caixa"
      rounded
      dense
    >
      <template #avatar><q-icon name="warning" color="orange-9" /></template>
      <div class="text-subtitle2 text-orange-10">Importação concluída com pendências</div>
      <div v-if="retornoImportacao && retornoImportacao.message" class="q-mt-xs">
        {{ retornoImportacao.message }}
      </div>
      <div class="q-mt-sm">
        Os lançamentos já foram gravados no caixa. Confira as diferenças antes de fechar o caixa.
      </div>
      <div class="q-mt-xs">
        Itens não mapeados não foram contabilizados no caixa.
      </div>
      <div class="row q-col-gutter-sm q-mt-sm">
        <div class="col-12 col-sm-4">
          <strong>Total de itens:</strong> {{ mapeamento.total_itens || 0 }}
        </div>
        <div class="col-12 col-sm-4">
          <strong>Mapeados:</strong> {{ mapeamento.mapeados || 0 }}
        </div>
        <div class="col-12 col-sm-4">
          <strong>Não mapeados:</strong> {{ mapeamento.nao_mapeados || 0 }}
        </div>
      </div>

      <div v-if="itensNegativos.length" class="q-mt-md itens-negativos">
        <div class="text-subtitle2 q-mb-xs">Itens menores que o importado pela sessão anterior (não lançados)</div>
        <q-markup-table dense flat bordered class="bg-white">
          <thead>
            <tr>
              <th class="text-left">Descrição</th>
              <th class="text-right">Quantidade</th>
              <th class="text-right">Valor</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in itensNegativos" :key="'negativo-' + index" class="bg-red-1 text-negative">
              <td>{{ item.descricao || item.produto_id || '-' }}</td>
              <td class="text-right">{{ item.quantidade }}</td>
              <td class="text-right">{{ formatarMoeda(item.valor) }}</td>
            </tr>
          </tbody>
        </q-markup-table>
        <div class="text-caption q-mt-xs">
          Cada um ficou menor que o importado pela sessão anterior (estorno ou correção da Caixa). Confira com a Caixa antes de fechar.
        </div>
      </div>

      <div v-if="naoMapeados.length" class="q-mt-md">
        <div class="text-subtitle2 q-mb-xs">Nomenclaturas não reconhecidas</div>
        <q-markup-table dense flat bordered class="bg-white">
          <thead>
            <tr>
              <th class="text-left">Seção</th>
              <th class="text-left">Descrição</th>
              <th class="text-left">Categoria</th>
              <th class="text-right">Quantidade</th>
              <th class="text-right">Valor</th>
              <th class="text-left">Motivo</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(item, index) in naoMapeados" :key="'nao-mapeado-' + index">
              <td>{{ item.secao || '-' }}</td>
              <td>{{ item.descricao || item.descricao_normalizada || '-' }}</td>
              <td>{{ item.categoria || item.categoria_id || '-' }}</td>
              <td class="text-right">{{ item.quantidade || 0 }}</td>
              <td class="text-right">{{ formatarMoeda(item.valor) }}</td>
              <td>{{ formatarMotivo(item.motivo) }}</td>
            </tr>
          </tbody>
        </q-markup-table>
      </div>

      <q-expansion-item
        v-if="divergenciasCategorias.length"
        dense
        expand-separator
        class="bg-white q-mt-md rounded-borders"
        label="Categorias divergentes"
        caption="Também destacadas em vermelho no resumo"
      >
        <q-markup-table dense flat bordered class="bg-white">
          <thead>
            <tr>
              <th class="text-left">Categoria</th>
              <th class="text-right">Qtd. Caixa</th>
              <th class="text-right">Qtd. gravada</th>
              <th class="text-right">Valor Caixa</th>
              <th class="text-right">Valor gravado</th>
              <th class="text-right">Diferença</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(item, index) in divergenciasCategorias"
              :key="'divergencia-categoria-' + index"
              :class="{ 'bg-red-1 text-negative': item.consistente === false }"
            >
              <td>{{ item.categoria || item.categoria_id || '-' }}</td>
              <td class="text-right">{{ item.quantidade_caixa || 0 }}</td>
              <td class="text-right">{{ item.quantidade_gravada || 0 }}</td>
              <td class="text-right">{{ formatarMoeda(item.valor_caixa) }}</td>
              <td class="text-right">{{ formatarMoeda(item.valor_gravado) }}</td>
              <td class="text-right">
                {{ item.diferenca_quantidade || 0 }} /
                {{ formatarMoeda(item.diferenca_valor) }}
              </td>
            </tr>
          </tbody>
        </q-markup-table>
      </q-expansion-item>

      <div v-if="errosGravacao.length" class="q-mt-md">
        <div class="text-subtitle2 q-mb-xs">Erros de gravação</div>
        <q-list dense bordered class="bg-white">
          <q-item v-for="(erroGravacao, index) in errosGravacao" :key="'erro-gravacao-' + index">
            <q-item-section>{{ erroGravacao.message || erroGravacao.mensagem || erroGravacao }}</q-item-section>
          </q-item>
        </q-list>
      </div>
    </q-banner>

    <!-- Nova tentativa: disponível quando ocioso ou após erro. A entrada na aba já
         dispara a primeira consulta; este botão existe para repetir. -->
    <q-btn
      v-if="!consultando && !relatorioCarregado"
      outline
      color="primary"
      icon="cloud_download"
      label="Importar resumo TFL"
      :loading="carregando"
      :disable="!podeConsultar"
      @click="$emit('consultar')"
      class="full-width"
    />
    <!-- Texto visível e não tooltip: botão desabilitado não dispara hover
         de forma confiável, e é o motivo que o usuário precisa ler. Antes
         daqui, clicar sem o agente pronto devolvia 400 em vermelho. -->
    <div
      v-if="!consultando && !relatorioCarregado && bloqueio"
      class="text-caption text-grey-8 q-mt-xs conexao-caixa-bloqueio"
    >
      <q-icon name="info" size="xs" class="q-mr-xs" />{{ bloqueio }}
    </div>

    <div v-if="relatorioCarregado && !consultando" class="row justify-center q-mt-md">
      <!-- Já importou: reenviar não muda nada, então o botão só confirma a leitura e fecha. -->
      <q-btn
        v-if="comPendencias"
        color="primary"
        icon="done"
        label="Ciente, vou conferir no caixa"
        @click="$emit('ciente')"
      />
      <q-btn
        v-else-if="podeLiberar"
        color="orange-9"
        icon="lock_open"
        label="Liberar e importar"
        :loading="enviando"
        :disable="!podeEnviar"
        @click="$emit('liberar')"
      />
      <q-btn
        v-else
        color="primary"
        icon="send"
        label="Enviar para o caixa"
        :loading="enviando"
        :disable="!podeEnviar"
        @click="$emit('enviar')"
      />
    </div>
  </div>
</template>

<script>
import * as importacaoCaixa from "./importacaoCaixa";

export default {
  name: "painel-conexao-caixa",

  emits: ["consultar", "enviar", "ciente", "liberar"],
  props: {
    consultando: { type: Boolean, default: false },
    mensagemProgresso: { type: String, default: "" },
    temErro: { type: Boolean, default: false },
    erro: { type: String, default: null },
    relatorioCarregado: { type: Boolean, default: false },
    carregando: { type: Boolean, default: false },
    podeConsultar: { type: Boolean, default: false },
    bloqueio: { type: String, default: "" },
    retornoImportacao: { type: Object, default: null },
    base: { type: Object, default: null },
    enviando: { type: Boolean, default: false },
    podeEnviar: { type: Boolean, default: false }
  },
  computed: {
    textoBase() {
      return importacaoCaixa.textoBase(this.base);
    },
    precisaLiberacao() {
      return !!(this.base && this.base.requer_liberacao);
    },
    podeLiberar() {
      return this.precisaLiberacao && !!this.base.pode_liberar;
    },
    textoLiberacao() {
      return importacaoCaixa.textoLiberacao(this.base);
    },
    itensNegativos() {
      return importacaoCaixa.itensNegativos(this.retornoImportacao);
    },
    comPendencias() {
      return importacaoCaixa.temPendencias(this.retornoImportacao);
    },
    naoMapeados() {
      return importacaoCaixa.naoMapeados(this.retornoImportacao);
    },
    divergenciasCategorias() {
      return importacaoCaixa.divergenciasCategorias(this.retornoImportacao);
    },
    errosGravacao() {
      return importacaoCaixa.errosGravacao(this.retornoImportacao);
    },
    mapeamento() {
      return importacaoCaixa.mapeamento(this.retornoImportacao);
    }
  },
  methods: {
    formatarMoeda: importacaoCaixa.formatarMoeda,
    formatarMotivo: importacaoCaixa.formatarMotivo
  }
};
</script>
